import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import './Users.css';

function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    email: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      if (response.data.success) {
        setUsers(response.data.users);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to load users');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/users', formData);
      if (response.data.success) {
        setFormData({ username: '', password: '', fullName: '', email: '' });
        setShowForm(false);
        setError(null);
        fetchUsers();
      } else {
        setError(response.data.message || 'Failed to create user');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await api.delete(`/users/${userId}`);
        if (response.data.success) {
          setError(null);
          fetchUsers();
        } else {
          setError(response.data.message || 'Failed to delete user');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const handleAddRole = (userId) => {
    // Navigate to ClientRoleManagement where user can assign roles
    // This would typically be done via a modal or separate page
    // For now, show alert directing user to role management page
    alert('Navigate to Roles & Permissions page to add roles to this user');
  };

  const handleRemoveRole = async (userId, groupId) => {
    if (window.confirm('Remove this role from the user?')) {
      try {
        const response = await api.post(`/users/${userId}/remove-role/${groupId}`);
        if (response.data.success) {
          setError(null);
          fetchUsers();
        } else {
          setError(response.data.message || 'Failed to remove role');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to remove role');
      }
    }
  };

  if (loading) {
    return <div className="users-container"><p>Loading users...</p></div>;
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h1>User Management</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Add New User'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {showForm && (
        <div className="user-form card mb-4">
          <div className="card-body">
            <h5 className="card-title">Create New User</h5>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="username" className="form-label">Username</label>
                <input
                  type="text"
                  className="form-control"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="fullName" className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              <button type="submit" className="btn btn-success">Create User</button>
            </form>
          </div>
        </div>
      )}

      <div className="users-table">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Roles</th>
              <th>Status</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map(u => (
                <tr key={u.UserID}>
                  <td>{u.UserName}</td>
                  <td>{u.FullName}</td>
                  <td>{u.Email}</td>
                  <td>
                    {u.UserGroups && u.UserGroups.length > 0 ? (
                      <div className="roles-container">
                        {u.UserGroups.map((group, idx) => (
                          <span key={idx} className="role-badge">
                            {group.GroupName}
                            <button
                              className="remove-role"
                              onClick={() => handleRemoveRole(u.UserID, group.GroupID)}
                              title="Remove role"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted">No roles</span>
                    )}
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleAddRole(u.UserID)}
                    >
                      + Add Role
                    </button>
                  </td>
                  <td>
                    <span className={`badge bg-${u.IsActive ? 'success' : 'danger'}`}>
                      {u.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(u.CreatedDate).toLocaleDateString()}</td>
                  <td>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(u.UserID)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Users;
